import { isTenantNameAvailable } from '@/actions/tenants';
import { getLandingName, MINIMUM_LANDING_NAME_LENGTH } from '@/utils/landing';
import { Button, FieldError, LoadingOverlay, TextInput } from '@payloadcms/ui';
import { useMemo, useState } from 'react';

import { WizardStepHeader } from './WizardStepHeader';

interface LandingNameFormProps {
  landingName: string;
  onLandingNameChange: (name: string) => void;
  onContinue: (name: string) => void;
  onBack: () => void;
}

export const LandingDisplayNameForm = ({ landingName, onLandingNameChange, onContinue, onBack }: LandingNameFormProps) => {
  const [landingNameError, setLandingNameError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isNameValid = useMemo(() => {
    return landingName.trim().length >= MINIMUM_LANDING_NAME_LENGTH;
  }, [landingName]);

  const internalName = useMemo(() => {
    return landingName.trim() ? getLandingName(landingName) : '';
  }, [landingName]);

  const handleLandingNameSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsSubmitting(true);

    setLandingNameError('');

    if (!isNameValid) {
      setLandingNameError(`Landing name must be at least ${MINIMUM_LANDING_NAME_LENGTH} characters long`);
      setIsSubmitting(false);
      return;
    }

    const isAvailable = await isTenantNameAvailable(landingName);
    if (!isAvailable) {
      setLandingNameError('Landing name is already taken, try another one.');
      setIsSubmitting(false);
      return;
    }

    setIsSubmitting(false);
    onContinue(landingName);
  };

  return (
    <div data-testid="create-new-landing-step-name">
      {isSubmitting && <LoadingOverlay />}
      <WizardStepHeader title="Enter the name of the landing" onBack={onBack} backDisabled={isSubmitting} />
      <form onSubmit={handleLandingNameSubmit}>
        <div data-testid="create-new-landing-name-field">
          <TextInput
            path="landing-name"
            label="Landing name"
            required
            value={landingName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onLandingNameChange(e.target.value)}
            description="The readable name of your landing. You can change it later. I.e: Landing Candlelight"
            Error={<FieldError path="landing-name" message={landingNameError} showError={!!landingNameError} />}
          />
        </div>
        {landingNameError && (
          <span data-testid="create-new-landing-name-error" hidden>
            {landingNameError}
          </span>
        )}
        <div data-testid="create-new-landing-internal-name-field">
          <TextInput
            path="landing-internal-name"
            label="Internal name"
            readOnly
            value={internalName}
            description="Auto-generated identifier used internally. This cannot be changed later."
          />
        </div>
        <div className="fv-create-new-landing-modal__step-actions">
          <Button
            type="submit"
            buttonStyle="primary"
            disabled={isSubmitting}
            extraButtonProps={{ 'data-testid': 'create-new-landing-name-continue' }}
          >
            Continue
          </Button>
        </div>
      </form>
    </div>
  );
};
